

import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { PageTitle, Button, Card, EmptyState } from '../components/common';
import { AuthContext } from '../contexts/AuthContext';
import { mockSupportTickets } from '../mockData';
import { SupportTicket, SupportTicketReply } from '../types';
import { useToast } from '../components/Toaster';

const TICKETS_PER_PAGE = 10;

// Icons
const GraduationCapIcon = ({ className = "w-8 h-8" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5"></path></svg>;
const BookOpenIcon = ({ className = "w-8 h-8" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>;
const CheckCircleIcon = ({ className = "w-8 h-8" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>;
const PlusCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" /></svg>;
const ViewIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" /></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const ChevronLeftIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>;
const ChevronRightIcon = () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
const XIcon = () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"></path></svg>;
const PaperclipIcon = () => <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>;


const StatCard: React.FC<{ title: string; value: number; icon: React.ReactElement; iconBg: string }> = ({ title, value, icon, iconBg }) => (
    <Card className="p-4 flex items-center">
        <div className={`p-3 rounded-lg ${iconBg} text-white mr-4`}>
            {icon}
        </div>
        <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
    </Card>
);

const TicketFormModal: React.FC<{ 
    isOpen: boolean; 
    onClose: () => void; 
    onSave: (ticket: SupportTicket) => void;
    ticketToEdit: SupportTicket | null;
}> = ({ isOpen, onClose, onSave, ticketToEdit }) => {
    const auth = useContext(AuthContext);
    const [subject, setSubject] = useState('');
    const [category, setCategory] = useState('Mailing Issues');
    const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
    const [description, setDescription] = useState('');
    const [attachment, setAttachment] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    useEffect(() => {
        if (ticketToEdit) {
            setSubject(ticketToEdit.subject);
            setCategory(ticketToEdit.category);
            setPriority(ticketToEdit.priority);
            setDescription(ticketToEdit.description);
            setAttachment(null);
        } else {
            setSubject('');
            setCategory('Mailing Issues');
            setPriority('Medium');
            setDescription('');
            setAttachment(null);
        }
    }, [ticketToEdit, isOpen]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setAttachment(e.target.files[0]);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const ticketData: SupportTicket = {
            id: ticketToEdit?.id || `#TIC${Math.floor(1000 + Math.random() * 9000)}`,
            userId: auth?.currentUser?.id || '',
            subject,
            category,
            priority,
            description,
            status: ticketToEdit?.status || 'Opened',
            createdDate: ticketToEdit?.createdDate || new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            replies: ticketToEdit?.replies || [],
            attachment: attachment ? { name: attachment.name, url: '#' } : ticketToEdit?.attachment,
        };
        onSave(ticketData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit}>
                    <header className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-lg font-bold text-gray-800">{ticketToEdit ? 'Edit Ticket' : 'Add New Ticket'}</h3>
                        <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XIcon /></button>
                    </header>
                    <div className="p-6 space-y-4">
                        <div>
                            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                            <input type="text" id="subject" value={subject} onChange={e => setSubject(e.target.value)} required className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select id="category" value={category} onChange={e => setCategory(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary">
                                    <option>Mailing Issues</option>
                                    <option>Language Issues</option>
                                    <option>Payment Issues</option>
                                    <option>Server Issues</option>
                                </select>
                            </div>
                             <div>
                                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                <select id="priority" value={priority} onChange={e => setPriority(e.target.value as any)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary">
                                    <option>High</option>
                                    <option>Medium</option>
                                    <option>Low</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea id="description" value={description} onChange={e => setDescription(e.target.value)} required rows={4} className="w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"></textarea>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
                            <div className="mt-1 flex items-center gap-4">
                                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                                    <PaperclipIcon /> Choose file
                                </Button>
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                                {attachment ? (
                                    <span className="text-sm text-gray-600">{attachment.name}</span>
                                ) : ticketToEdit?.attachment ? (
                                     <span className="text-sm text-gray-600">{ticketToEdit.attachment.name}</span>
                                ) : (
                                    <span className="text-sm text-gray-500">No file chosen</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <footer className="p-4 border-t flex justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                        <Button type="submit">{ticketToEdit ? 'Save Changes' : 'Submit Ticket'}</Button>
                    </footer>
                </form>
            </div>
        </div>
    );
};

const ViewTicketModal: React.FC<{ ticket: SupportTicket | null; isOpen: boolean; onClose: () => void; }> = ({ ticket, isOpen, onClose }) => {
    if (!isOpen || !ticket) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold text-gray-800">{ticket.subject}</h3>
                    <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XIcon /></button>
                </header>
                <div className="p-6 overflow-y-auto">
                    <div className="mb-4">
                        <h4 className="font-semibold text-gray-700">Description</h4>
                        <p className="text-gray-600 whitespace-pre-wrap">{ticket.description}</p>
                    </div>

                    {ticket.attachment && (
                        <div className="mb-4">
                            <h4 className="font-semibold text-gray-700">Attachment</h4>
                            <a href={ticket.attachment.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-600 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded">
                                <PaperclipIcon /> {ticket.attachment.name}
                            </a>
                        </div>
                    )}

                    <hr className="my-4"/>
                    <h4 className="font-semibold text-gray-700 mb-2">Replies</h4>
                    <div className="space-y-4">
                        {ticket.replies && ticket.replies.length > 0 ? ticket.replies.map(reply => (
                             <div key={reply.id} className="flex items-start gap-3">
                                <img src={reply.authorAvatar} alt={reply.authorName} className="w-8 h-8 rounded-full" />
                                <div className="flex-1 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                    <div className="flex justify-between items-center mb-1">
                                        <p className="font-semibold text-sm text-gray-800">{reply.authorName}</p>
                                        <p className="text-xs text-gray-400">{new Date(reply.timestamp).toLocaleString()}</p>
                                    </div>
                                    <p className="text-sm text-gray-700">{reply.text}</p>
                                </div>
                            </div>
                        )) : (
                            <p className="text-sm text-gray-500">No replies yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DeleteConfirmationModal: React.FC<{ ticket: SupportTicket | null; isOpen: boolean; onClose: () => void; onConfirm: () => void; }> = ({ ticket, isOpen, onClose, onConfirm }) => {
    if (!isOpen || !ticket) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900">Delete Ticket</h3>
                    <p className="mt-2 text-sm text-gray-600">Are you sure you want to delete the ticket "{ticket.subject}"? This action cannot be undone.</p>
                </div>
                <footer className="p-4 bg-gray-50 border-t flex justify-end gap-2">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="button" variant="danger" onClick={onConfirm}>Delete</Button>
                </footer>
            </div>
        </div>
    );
};

const Pagination: React.FC<{ currentPage: number; totalPages: number; onPageChange: (page: number) => void }> = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    return (
        <div className="flex flex-wrap justify-between items-center mt-6 px-6 pb-4 gap-4">
            <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
            </p>
            <nav className="flex items-center space-x-1">
                <button 
                    onClick={() => onPageChange(currentPage - 1)} 
                    disabled={currentPage === 1} 
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Previous page"
                >
                    <ChevronLeftIcon />
                </button>
                {pageNumbers.map(number => (
                    <button
                        key={number}
                        onClick={() => onPageChange(number)}
                        className={`w-9 h-9 text-sm font-medium rounded-full flex items-center justify-center transition-colors ${currentPage === number ? 'bg-primary text-white shadow-sm' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        aria-current={currentPage === number ? 'page' : undefined}
                    >
                        {number}
                    </button>
                ))}
                 <button 
                    onClick={() => onPageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages} 
                    className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-500"
                    aria-label="Next page"
                >
                    <ChevronRightIcon />
                </button>
            </nav>
        </div>
    );
};

const SupportTicketsPage: React.FC = () => {
    const auth = useContext(AuthContext);
    const toast = useToast();
    const [currentPage, setCurrentPage] = useState(1);
    
    const [userTickets, setUserTickets] = useState<SupportTicket[]>(() => 
        mockSupportTickets
            .filter(ticket => ticket.userId === auth?.currentUser?.id)
            .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
    );

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

    const stats = useMemo(() => {
        const total = userTickets.length;
        const opened = userTickets.filter(t => t.status === 'Opened' || t.status === 'Inprogress').length;
        const closed = total - opened;
        return { total, opened, closed };
    }, [userTickets]);

    const paginatedTickets = useMemo(() => {
        const startIndex = (currentPage - 1) * TICKETS_PER_PAGE;
        return userTickets.slice(startIndex, startIndex + TICKETS_PER_PAGE);
    }, [userTickets, currentPage]);
    
    const totalPages = Math.ceil(userTickets.length / TICKETS_PER_PAGE);

    const getStatusBadge = (status: SupportTicket['status']) => {
        const styles = { 'Opened': 'bg-purple-100 text-purple-800', 'Inprogress': 'bg-yellow-100 text-yellow-800', 'Closed': 'bg-green-100 text-green-800' };
        return styles[status] || 'bg-gray-100 text-gray-800';
    };

    const getPriorityBadge = (priority: SupportTicket['priority']) => {
        const styles = { 'High': 'bg-red-100 text-red-800', 'Medium': 'bg-blue-100 text-blue-800', 'Low': 'bg-green-100 text-green-800' };
        return styles[priority] || 'bg-gray-100 text-gray-800';
    };

    const handleOpenAddModal = () => {
        setSelectedTicket(null);
        setIsFormModalOpen(true);
    };

    const handleOpenEditModal = (ticket: SupportTicket) => {
        setSelectedTicket(ticket);
        setIsFormModalOpen(true);
    };

    const handleOpenViewModal = (ticket: SupportTicket) => {
        setSelectedTicket(ticket);
        setIsViewModalOpen(true);
    };

    const handleOpenDeleteModal = (ticket: SupportTicket) => {
        setSelectedTicket(ticket);
        setIsDeleteModalOpen(true);
    };

    const handleSaveTicket = (ticketData: SupportTicket) => {
        const isEditing = !!selectedTicket;
        setUserTickets(prev => {
            if (isEditing) {
                return prev.map(t => t.id === ticketData.id ? ticketData : t);
            }
            return [ticketData, ...prev];
        });
        toast.success(`Ticket ${isEditing ? 'updated' : 'created'} successfully!`);
        setIsFormModalOpen(false);
        setSelectedTicket(null);
    };

    const handleDeleteConfirm = () => {
        if (!selectedTicket) return;
        setUserTickets(prev => prev.filter(t => t.id !== selectedTicket.id));
        toast.success(`Ticket "${selectedTicket.subject}" deleted.`);
        setIsDeleteModalOpen(false);
        setSelectedTicket(null);
    };

    return (
        <div className="space-y-6">
            <PageTitle title="Support Tickets">
                <Button onClick={handleOpenAddModal}>
                    <PlusCircleIcon />
                    Add Ticket
                </Button>
            </PageTitle>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                <StatCard title="Total Tickets" value={stats.total} icon={<GraduationCapIcon />} iconBg="bg-blue-500" />
                <StatCard title="Opened Tickets" value={stats.opened} icon={<BookOpenIcon />} iconBg="bg-pink-500" />
                <StatCard title="Closed Tickets" value={stats.closed} icon={<CheckCircleIcon />} iconBg="bg-green-500" />
            </div>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 font-semibold">
                            <tr>
                                <th scope="col" className="px-6 py-4">Ticket ID</th>
                                <th scope="col" className="px-6 py-4">Date</th>
                                <th scope="col" className="px-6 py-4">Subject</th>
                                <th scope="col" className="px-6 py-4">Priority</th>
                                <th scope="col" className="px-6 py-4">Category</th>
                                <th scope="col" className="px-6 py-4">Status</th>
                                <th scope="col" className="px-6 py-4 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedTickets.length > 0 ? (
                                paginatedTickets.map(ticket => (
                                    <tr key={ticket.id} className="bg-white border-b hover:bg-gray-50">
                                        <td className="px-6 py-4 font-bold text-indigo-600">{ticket.id}</td>
                                        <td className="px-6 py-4 text-gray-600 whitespace-nowrap">{new Date(ticket.createdDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                        <td className="px-6 py-4 text-gray-800 font-medium">{ticket.subject}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(ticket.priority)}`}>
                                                <span className="w-2 h-2 mr-1.5 rounded-full bg-current"></span>
                                                {ticket.priority}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{ticket.category}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusBadge(ticket.status)}`}>
                                                <span className="w-2 h-2 mr-1.5 rounded-full bg-current"></span>
                                                {ticket.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center space-x-3 text-gray-500">
                                                <button onClick={() => handleOpenViewModal(ticket)} className="hover:text-primary" aria-label="View ticket"><ViewIcon /></button>
                                                <button onClick={() => handleOpenEditModal(ticket)} className="hover:text-primary" aria-label="Edit ticket"><EditIcon /></button>
                                                <button onClick={() => handleOpenDeleteModal(ticket)} className="hover:text-red-500" aria-label="Delete ticket"><DeleteIcon /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="py-10">
                                        <EmptyState title="No Support Tickets" message="You have not created any support tickets yet." />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                 {totalPages > 1 && (
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                )}
            </Card>

            <TicketFormModal isOpen={isFormModalOpen} onClose={() => setIsFormModalOpen(false)} onSave={handleSaveTicket} ticketToEdit={selectedTicket} />
            <ViewTicketModal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} ticket={selectedTicket} />
            <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} onConfirm={handleDeleteConfirm} ticket={selectedTicket} />
        </div>
    );
};

export default SupportTicketsPage;